import apiClient, { getErrorMessage } from './client';

export type CompanyReportSection = 'all' | 'incoming' | 'outgoing' | 'fx' | 'ledger';

export interface CompanyActivityReportQuery {
  clientId: string;
  accountId?: string;
  from: string;
  to: string;
  section: CompanyReportSection;
  page: number;
  pageSize: number;
}

export interface CompanyActivityReportRow {
  rowType: string;
  id: string;
  occurredAt: string;
  clientAccountId: string;
  accountRole: string;
  reference: string;
  amount: number;
  currency: string;
  status: string;
  counterparty?: string;
  operation?: string;
  entrySide?: string;
  businessObjectType?: string;
  businessObjectId?: string;
  clientRate?: number;
  providerRate?: number;
  profitAmount?: number;
  profitCurrency?: string;
}

export interface CompanyActivityReportResponse {
  items: CompanyActivityReportRow[];
  totalCount: number;
  page: number;
  pageSize: number;
}

function toParams(query: CompanyActivityReportQuery): Record<string, string | number> {
  return {
    clientId: query.clientId,
    ...(query.accountId ? { accountId: query.accountId } : {}),
    from: query.from,
    to: query.to,
    section: query.section,
    page: query.page,
    pageSize: query.pageSize,
  };
}

function filenameFromDisposition(value: unknown): string {
  const header = String(value ?? '');
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ?? 'company-activity.csv';
}

export const companyReportsApi = {
  async get(query: CompanyActivityReportQuery): Promise<CompanyActivityReportResponse> {
    try {
      const response = await apiClient.get<CompanyActivityReportResponse>('/admin/reports/company', { params: toParams(query) });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load company activity report.'));
    }
  },

  async downloadCsv(query: CompanyActivityReportQuery): Promise<void> {
    try {
      const response = await apiClient.get('/admin/reports/company/export', {
        params: toParams(query),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filenameFromDisposition(response.headers['content-disposition']);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to download CSV report.'));
    }
  },
};
